/**
 * The manual-save combat guard (D-617 Q5, spec §4.1): the player counts as in combat while a hostile ship is within
 * `hostileM` or the player was hit in the last `hitS` seconds. The numbers are the scanner's combat rule
 * (`reach-explore.json` `scanner.combatHostileM` / `combatHitS`), so "in combat" means one thing across the game.
 * `watchPlayerHits` runs every tick and allocates nothing (D-014).
 */
import { EV, EVENT_MASK, firstReadable } from '../../combat/events'
import { loadExploreConfig } from '../../exploration/config'
import { hostileWithin } from '../../exploration/scanner'
import type { Sim } from '../../sim'

export interface CombatGuardConfig {
  readonly hostileM: number
  readonly hitS: number
}

export interface CombatGuard {
  readonly cfg: CombatGuardConfig
  /** Sim time of the last hit on the player. */
  lastHitAt: number
}

export function combatGuardConfig(): CombatGuardConfig {
  const s = loadExploreConfig().scanner
  return { hostileM: s.combatHostileM, hitS: s.combatHitS }
}

/** Records hits on the player from the combat ring since `cursor` (the caller advances its cursor). */
export function watchPlayerHits(g: CombatGuard, sim: Sim, cursor: number): void {
  const ring = sim.events
  const p = sim.playerEid
  if (p < 0) return
  for (let i = firstReadable(ring, cursor); i < ring.head; i++) {
    const slot = i & EVENT_MASK
    const type = ring.type[slot]
    if ((type === EV.hitShield || type === EV.hitHull) && ring.eid[slot] === p) g.lastHitAt = sim.time
  }
}

export function playerInCombat(g: CombatGuard, sim: Sim): boolean {
  const p = sim.playerEid
  if (p < 0) return false
  return sim.time - g.lastHitAt < g.cfg.hitS || hostileWithin(sim, p, g.cfg.hostileM)
}
